import * as React from 'react';
import { ChangeEvent, useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import TagManager from 'react-gtm-module';
import { FormattedMessage, useIntl } from 'react-intl';
import { MenuItem, Button, Menu, Typography, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { Person } from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import isEmail from 'validator/lib/isEmail';
import { HtmlTooltip } from './HtmlTooltip';
import { AppVersionContext } from '../context/AppVersionContext';
import * as GolferHelper from '../library/GolferHelper';
import * as WedgeOrderHelper from '../library/WedgeOrderHelper';
import * as IosHelper from '../library/IosHelper';
import * as FitterHelper from '../library/FitterHelper';
import * as TutorialHelper from '../library/TutorialHelper';
import * as TrackManHelper from '../library/TrackManHelper';
import * as VersionChecker from '../library/VersionChecker';
import * as SimulatorHelper from '../library/SimulatorHelper';
import Golfer from '../classes/Golfer';
import Fitter from '../classes/Fitter';
import Checkbox from './Checkbox';
import setupPdfEN from "../images/SetupInstructions.pdf";
import setupPdfJP from "../images/SetupInstructionsJapanese.pdf";
import setupPdfFR from "../images/SetupInstructionsFrench.pdf";
import { Locale, LocaleType } from '../classes/Locale';
import { IPipaConsentDialogState, PipaConsentDialog } from './PipaConsentDialog';
import { PipaConsent, IPipaConsent } from '../classes/PipaConsent';

import './GolferInfo.css';

interface IMenuProps {
    id: string;
    MenuListProps: any;
    anchorEl: any;
    open: any;
    onClose: any;
}

const StyledMenu = styled((props: IMenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
    },
}));

export const getSetupInstructionsPDF = (locale: LocaleType) => {
    switch (locale) {
        case Locale.frenchCanadian:
        case Locale.french:
          return setupPdfFR;
        case Locale.japanese:
          return setupPdfJP;
        default:
          return setupPdfEN;
      }
}

export default function GolferInfo() {
    const { appVersion } = useContext(AppVersionContext);
    const [golfer, setGolfer] = useState(new Golfer());
    const [nameIsValid, setNameIsValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const [iOS, setiOS] = useState(false);
    const [phoneIsValid, setPhoneIsValid] = useState(false);
    const [fitter, setFitter] = useState(new Fitter());
    const [showSMSOption, setShowSMSOption] = useState(false);
    const [setupInstructionsPDF, setSetupInstructionsPDF] = useState();
    const [pipaConsentDialog, setPipaConsentDialog] = useState<null | { show: boolean } & IPipaConsentDialogState>(null);

    const isPipaConsentRequired = fitter?.country?.toUpperCase() === "KR";
    const isPipaConsentFilled = pipaConsentDialog?.data && !pipaConsentDialog.show && Object.values(pipaConsentDialog.data).every(value => value !== "");
    
    const intl = useIntl();
    const open = Boolean(anchorEl);
    const navigate = useNavigate();

    const intlSettings = intl.formatMessage({ id: "general.settings" });
    const intlFitterHistory = intl.formatMessage({ id: "history.title" });
    const intlQuickStartGuide = intl.formatMessage({ id: "quick.start.title" });
    const intlSetupInstructions = intl.formatMessage({ id: "settings.connection.setup.instructions" });
    const intlLogout = intl.formatMessage({ id: "general.logout" });
    const intlSurefitHubHome = intl.formatMessage({ id: "general.surefit.hub.home" });
    const intlPrivacyPolicyUrl = intl.formatMessage({ id: "general.privacy.policy.url" });

    useEffect((): void => {

        const getAsyncData = async () => {
            const newFitter = await FitterHelper.getFitter();
            setFitter(newFitter);

            // Get "Setup Instructions" pdf url
            const setupInstructionsPDF = getSetupInstructionsPDF(newFitter.locale);
            setSetupInstructionsPDF(setupInstructionsPDF);

            const iOS = await IosHelper.get();
            setiOS(iOS);

            // set SMS OptIn after we identify iOS
            setShowSMSOption(newFitter.country === "US" && !iOS);
            
            if (!iOS) {
                await FitterHelper.logOutIfExpiredLogin(navigate);
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_golferinfo'
                }
            })

            setShowTutorial(await TutorialHelper.isActive());

            //if coming to this page without a fitter, redirect to login
            if (newFitter.fitterID === 0) {
                navigate("/apps/wedgefitting");
            }
            let newGolfer = await GolferHelper.newGolfer();
            newGolfer.fitterId = newFitter.fitterID;
            newGolfer.fitterEmail = newFitter.email;
            setGolfer(newGolfer);

            //send messages in queue if online 
            refreshFittingQueue(250);
        }
        getAsyncData();
    }, []);

    useEffect(() => {
        setPipaConsentDialog(
            isPipaConsentRequired
                ? { show: false, data: new PipaConsent(), }
                : null
        );
      
    }, [isPipaConsentRequired])
    

    //user is on iOS and offline. Wait for them to go back online and clear the fittings queue
    const manageFittingQueue = async () => {
        //        debugger;
        let fittings = await WedgeOrderHelper.getInQueueFittings();
        if (fittings.length > 0) {
            if ((window as any).navigator.onLine) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_info',
                        description: 'Golfer info - place queued orders in page',
                        msg: 'order count: ' + fittings.length.toString()
                    }
                });

                let tmConfiguration = await TrackManHelper.getConfiguration();
                try {
                    let simulator = await SimulatorHelper.getSimulator();
                    let currentHref = window.location.href;
                    await WedgeOrderHelper.placeAllQueuedOrders(0, tmConfiguration, simulator);
                    //run again to turn off the spinner if all orders were placed, or try again if any didn't get sent
                    let fittings = await WedgeOrderHelper.getInQueueFittings();
                    refreshFittingQueue(20000);
                }
                catch (err: any) {
                    TagManager.dataLayer({
                        dataLayer: {
                            event: 'log_error',
                            description: 'Golfer Info - process queued orders in page',
                            msg: 'catch: ' + err
                        }
                    });
                }
            } else {
                refreshFittingQueue(10000);
            }
        }
    }

    //due to this being called recursively, it will run forever and chew up memory. 
    //Do a check that if they are no longer on 'golferinfo' page, then don't call it anymore
    const refreshFittingQueue = (ms: number) => {
        setTimeout(() => {
            if (window.location.href.includes('golferinfo')) {
                manageFittingQueue();
            }
        }, ms)
    }

    const handleFitterButton = (event: any) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleBack = () => {
        navigate('/apps/wedgefitting');
    }

    const handleSubmit = async () => {
        // Check for login expiration if not iOS
        if (!iOS) {
            await FitterHelper.logOutIfExpiredLogin(navigate);
        }
        let isValid = validatePage(golfer);

        setShowErrors(!isValid);
        //if email is empty, then prompt if wants to enter their email or not
        if (isValid) {
            if ((fitter.hideGolferEmail === undefined || !fitter.hideGolferEmail) && golfer.email === '') {
                setDialogOpen(true);
            } else {
                submitPage();
            }
        }
    }

    const validateEmail = (email: string): boolean => {
        let validEmail = email === "" || isEmail(email);
        setEmailIsValid(validEmail)
        return validEmail;
    }

    const validatePage = (golfer: Golfer): boolean => {
        //validate the full name and email address
        //if email is shown to user, validate it        
        let validEmail = true;
        if (fitter.hideGolferEmail === undefined || !fitter.hideGolferEmail) {
            validEmail = validateEmail(golfer.email);
        }

        let validName = golfer.firstName.trim() + golfer.lastName.trim() != ''; 
        setNameIsValid(validName);

        let validPhoneNumber = !golfer.smsOptIn || (golfer.smsOptIn && golfer.phone != null && golfer.phone !== "");
        setPhoneIsValid(validPhoneNumber);

        let pipaConsentValid = (pipaConsentDialog && isPipaConsentRequired) ? isPipaConsentFilled : true;

        let isValid = validName && validPhoneNumber  && validEmail && !!pipaConsentValid;

        return isValid;
    }

    const handleFormChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newGolfer = { ...golfer, [target.name]: target.value } as Golfer;
        validatePage(newGolfer); //refresh form error messages if necessary
        setGolfer(newGolfer);
    }

    //if app is online, check if new version is needed
    const submitPage = async () => {
        //register the client info with GA. This has be done after the golfer logs in b/c it takes a few seconds after the fitter logs in for cookies to be set. Do it too early and the cookies are empty
        var clientId = "";
        if (fitter.userPool === 1) {
            clientId = fitter.fitterID.toString();
        } else {
            let cookies = document.cookie.match(/_ga=(.+?);/) || [];
            if (cookies.length > 1) {
                clientId = cookies[1].split('.').slice(-2).join(".")
            }
        }

        const tagManagerArgs = {
            gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER || "",
            dataLayer: {
                userId: clientId
            }
        }
        TagManager.initialize(tagManagerArgs);

        //save golfer login info
        //convert fullname to first/last name
        golfer.fullName = (golfer.firstName + " " + golfer.lastName).trim();

        // * note: This is hardcoded due to incorrect state management in React, where the property is not updated by the state in time to be used later when the next steps fetch the data from IndexedDB.
        golfer.consentDataCollection = pipaConsentDialog?.data?.consentDataCollection ?? "";
        golfer.consentDataThirdparty = pipaConsentDialog?.data?.consentDataThirdparty ?? "";
        golfer.consentDataInternational = pipaConsentDialog?.data?.consentDataInternational ?? "";
        
        //save to indexdb
        await GolferHelper.upsertGolfer(golfer);
        setTimeout(() => {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_session_start',
                    golfer_id: golfer.id,
                    fit_id: golfer.orderId
                }
            });
        }, 1000);

        //check if we need to download a new version
        if (window.navigator.onLine && !iOS) {
            let needNewVersion = await VersionChecker.needNewVersion();
            if (!needNewVersion) {
                navigate('/apps/wedgefitting/golferpreferences');
            } else {
                let versionInfo = new VersionChecker.VersionInfo(new Date(Date.now() - (3600 * 1000 * 24)), 0, "/apps/wedgefitting/golferpreferences");
                await VersionChecker.setVersionInfo(versionInfo);
                navigate('/apps/wedgefitting/refreshversion');
            }
        } else {
            navigate("/apps/wedgefitting/golferpreferences");
        }
    }

    const handleFittingProcess = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_fitting_process',
            }
        });
        navigate('/apps/wedgefitting/fittingprocess');
    }

    const handleFitterHistory = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_fitter_history',
            }
        });
        navigate('/apps/wedgefitting/fitterhistory');
    }

    const handleDialogClose = () => {
        setDialogOpen(false);
    }

    const handleQuickStartGuide = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_quick_start_guide'
            }
        })
        handleClose();
        TutorialHelper.enable();
        setShowTutorial(true);
    }

    const handleSetupInstructions = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_setup_instructions'
            }
        })
        handleClose();
        window.open("/apps/wedgefitting/setupinstructions");
    }

    const handleLogout = async () => {
        if (iOS) {
            try {
                (window as any).webkit.messageHandlers.iOSCloseView.postMessage(true);
            } catch { }
        } else {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_logout'
                }
            })
            await FitterHelper.logout();
            navigate('/apps/wedgefitting');
        }
    }

    //user selected to not enter their email address and continue to the next screen
    const handleDialogContinue = () => {
        submitPage();
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId === id;
    }

    const handleTutorialNext = () => {
        if (tutorialId === 1) {
            navigate('/apps/wedgefitting/golferpreferences');
        }
        setTutorialId(tutorialId + 1);
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
    }

    const handleSMSChange = () => {
        const newGolfer = { ...golfer, smsOptIn: !golfer.smsOptIn };
        setGolfer(newGolfer);
    }

    const handlePipaConsentChange = (updatedData: IPipaConsent) => {
        setPipaConsentDialog(prev =>
            prev ? { ...prev, show: prev.show, data: updatedData } : null
        );
    };

    const handlePipaConsentCancel = (state: IPipaConsentDialogState) => {
        setPipaConsentDialog(prev => ({
            ...prev,
            show: false,
            data: new PipaConsent(),
        }));
    };

    const handlePipaConsentConfirm = (state: IPipaConsentDialogState) => {
        setPipaConsentDialog(prev => ({
            ...prev,
            show: false,
            data: state.data,
        }));
    };

    const handleOnClickPipaConsentLabel = () =>
        setPipaConsentDialog(prev => (prev ? { ...prev, show: true } : null));

    let buttonStartFittingClasses = "titleist-btn input-tall golfer-registration--button";
    if(isPipaConsentRequired && !isPipaConsentFilled) {
        buttonStartFittingClasses += " golfer-registration--button--disabled";
    }

    return (
        <div className='golfer-info'>
            <div className="navbar">
                <div className="container">
                    <div>
                        <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex', marginTop: '0px' } }}>
                            <Person sx={{ width: '30px', marginRight: '5px', marginTop: '0px', height: '2em' }}></Person>
                            <Button disableRipple type="submit" className="fitter-button" endIcon={<KeyboardArrowDownIcon />} onClick={handleFitterButton}>{fitter.name}</Button>

                            <StyledMenu
                                id="demo-customized-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'demo-customized-button',
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                            >
                                <MenuItem onClick={handleFittingProcess} disableRipple>
                                    {intlSettings}
                                </MenuItem>
                                <MenuItem onClick={handleFitterHistory} disableRipple>
                                    {intlFitterHistory}
                                </MenuItem>
                                <MenuItem onClick={handleQuickStartGuide} disableRipple>
                                    {intlQuickStartGuide}
                                </MenuItem>
                                {!fitter.iOS &&
                                    <MenuItem onClick={handleSetupInstructions} disableRipple>
                                        {intlSetupInstructions}
                                    </MenuItem>
                                }
                                <MenuItem onClick={handleLogout} disableRipple>
                                    {iOS ? intlSurefitHubHome : intlLogout}
                                </MenuItem>

                            </StyledMenu>
                        </Typography>
                    </div>
                    <div className="navbar--center">
                        <div className="logo" />
                    </div>
                    <div></div>
                </div>
            </div>

            <div className="golfer-registration--container">
                <div className="golfer-registration--wrapper">

                    <div style={{ position: "relative" }}>
                        <div className="golfer-registration--main-image" />
                        <div style={{ position: "absolute", top: "25px", width: "100%" }}>
                            <div style={{ width: "100%" }}>
                                <span style={{ fontSize: "x-large", fontWeight: "600" }}>Wedge Fitting</span>&nbsp;
                                <span>by</span>
                                <span className="titleist-logo"></span>
                            </div>
                        </div>
                        <span className="golfer-registration--app-version">Version {appVersion}</span>
                    </div>
                    <div className="golfer-registration--inputs">

                        <HtmlTooltip
                            title={
                                <>
                                    <Typography color="inherit" sx={{ paddingBottom: '10px' }}><FormattedMessage id="quick.start.thank.you" /></Typography>
                                    <Typography color="inherit" sx={{ height: '200px' }}><FormattedMessage id="quick.start.basics" /></Typography>
                                    <br />
                                    <div style={{ textAlign: 'right' }}>
                                        <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                        <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                    </div>
                                </>
                            }
                            open={showPopup(0)}
                            placement={'top-start'}
                            arrow={false}
                            maxwidth={800}
                            left={"-200px"}
                        ><span>&nbsp;</span>
                        </HtmlTooltip>
                        <HtmlTooltip
                            title={
                                <>
                                    <Typography color="inherit"><FormattedMessage id="quick.start.recommendations" /></Typography>
                                    <br />
                                    <div style={{ textAlign: 'right' }}>
                                        <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                        <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                    </div>
                                </>
                            }
                            open={showPopup(1)}
                            placement={'left-start'}
                            arrow={false}
                            maxwidth={300}
                        >
                            <div className="golfer-registration--input">
                                <label className="titleist-label"><FormattedMessage id="general.golfer.first.name" /></label>
                                <input type="text" className="titleist-input input-tall-important" onChange={handleFormChange} name="firstName" value={golfer.firstName} />
                            </div>
                        </HtmlTooltip>
                        <div className="golfer-registration--input">
                            <label className="titleist-label"><FormattedMessage id="general.golfer.last.name" /></label>
                            <input className="titleist-input input-tall-important" type="text" onChange={handleFormChange} name="lastName" value={golfer.lastName} />
                        </div>
                        <div className="golfer-registration--input">
                            <label className="titleist-label"><FormattedMessage id="general.golfer.email" /></label>
                            <input className="titleist-input input-tall-important" type="text" onChange={handleFormChange} name="email" value={golfer.email} />
                        </div>
                        {showSMSOption &&
                            <>
                                <div className="golfer-registration--input">
                                    <label className="titleist-label"><FormattedMessage id="general.golfer.phone" /></label>
                                    <div className="golfer-registration--phone-number">
                                        <label>+1</label>
                                        <input className="titleist-input input-tall-important" type="text" onChange={handleFormChange} name="phone" value={golfer.phone} />
                                    </div>
                                </div>
                                <Checkbox checked={golfer.smsOptIn || false} onChange={handleSMSChange}>
                                    <div>
                                        <b><FormattedMessage id="general.sms.updates" /></b>&nbsp;
                                        <span className="golfer-registration--sms-message"><FormattedMessage id="general.sms.message" /></span>
                                    </div>
                                </Checkbox>
                                <div className="golfer-registration--sms-notice">
                                    <FormattedMessage id="general.sms.notice.1" />&nbsp;
                                    <FormattedMessage id="general.sms.notice.2" />&nbsp;
                                    <span style={{color: "red"}}><FormattedMessage id="general.privacy.policy" /></span>&nbsp;
                                    <FormattedMessage id="general.and"></FormattedMessage>&nbsp;
                                    <span style={{color: "red"}}><FormattedMessage id="general.sms.terms.and.conditions" /></span>&nbsp;
                                    <FormattedMessage id="general.sms.notice.3" />
                                </div>
                            </>}
                        {!nameIsValid && showErrors &&
                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                <span><FormattedMessage id="general.enter.name" /></span>
                            </Typography>
                        }
                        {!emailIsValid && showErrors &&
                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                <span><FormattedMessage id="general.email.invalid" /></span>
                            </Typography>
                        }
                        {!phoneIsValid && showErrors &&
                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                <span><FormattedMessage id="general.phone.invalid" /></span>
                            </Typography>
                        }
                        {isPipaConsentRequired && !isPipaConsentFilled && showErrors && (
                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                <span><FormattedMessage id="pipa.consent.not.filled.error.msg" /></span>
                            </Typography>
                        )}
                        <div style={{ textAlign: "left" }}>
                            <button className={buttonStartFittingClasses} onClick={handleSubmit}>
                                <FormattedMessage id="general.start.fitting" /> {">"}
                            </button>
                        </div>
                        {iOS &&
                            <div style={{ marginRight: "auto", marginTop: "10px" }}>
                                <a href={setupInstructionsPDF} target="_blank" style={{ fontSize: '12px' }} rel="noreferrer">{intlSetupInstructions}</a>
                            </div>
                        }

                        <div style={{ marginTop: "auto", marginBottom: "5%", maxWidth: "500px" }}>
                            <span style={{ fontSize: '12px' }}>
                                <FormattedMessage id="general.personal.data" />
                            </span> <a href={intlPrivacyPolicyUrl} target="_blank" style={{ fontSize: '12px', color: "red" }} rel="noreferrer"><FormattedMessage id="general.privacy.policy" /></a>

                        </div>

                        {isPipaConsentRequired && (
                            <div className="golfer-registration--pipa-notice">
                                <button type='button' onClick={handleOnClickPipaConsentLabel}><span><FormattedMessage id="pipa.cta.text" /></span></button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {
                isPipaConsentRequired && 
                pipaConsentDialog && 
                <PipaConsentDialog
                    show={pipaConsentDialog.show}
                    data={pipaConsentDialog.data}
                    onChange={handlePipaConsentChange}
                    onCancel={handlePipaConsentCancel}
                    onConfirm={handlePipaConsentConfirm}
                /> 
            }

            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        <FormattedMessage id="general.need.email" />
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='cancel-button' onClick={handleDialogClose} sx={{ color: 'black', borderColor: 'black' }}><FormattedMessage id="general.back" /></Button>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleDialogContinue} sx={{ color: 'black', borderColor: 'black' }} ><FormattedMessage id="general.continue" /></Button>
                </DialogActions>
            </Dialog>
        </div >
    )
}
